import { useRef, useContext, useState, useEffect } from "react"
import { record } from "rrweb"

import { AuthContext } from "../../context/AuthContext"
import { SessionContext } from "../../context/SessionContext"
import { CodeContext } from "./context/CodeContext"
import { TerminalContext } from "./context/TerminalContext"
import { ChatContext } from "./context/ChatContext"
import { TelemetryContext } from "./context/TelemetryContext"
import { ConsentContext } from "./context/ConsentContext"
import { DemographicSurveyContext } from "./context/DemographicSurveyContext"
import { SurveyContext } from "./context/SurveyContext"
import { QuizContext } from "./context/QuizContext"

import TaskDescription from "./components/TaskDescription"
import ProvidedCode from "./components/ProvidedCode"
import TestCases from "./components/TestCases"
import CodeEditor from "./components/CodeEditor"
import Terminal from "./components/Terminal"
import ChatWindow from "./components/ChatWindow"
import OffloadingStrip from "./components/OffloadingStrip"
import ReplayModal from "./components/ReplayModal"
import ConsentModal from "../student-components/ConsentModal"
import DemographicSurvey from "./components/DemographicSurvey"
import SurveyModal from "./components/SurveyModal"
import QuizModal from "./components/QuizModal"
import PrizeWheelModal from "./components/PrizeWheelModal"
import InstructionsModal from "./components/InstructionsModal"
import LivePreview from "./components/LivePreview"
import ProbeModal from "./study/ProbeModal"
import ProbeReview from "./study/ProbeReview"
import StudySurvey from "./study/StudySurvey"
import StudyInstructions, { hasSeenInstructions, markInstructionsSeen } from "./study/StudyInstructions"
import CcChat from "./study/cc/CcChat"
import { useStudySurvey } from "./study/useStudySurvey"
import { getStudyConfig, isCcStudy } from "./study/studyConfig"
import ArrowForward from "@mui/icons-material/ArrowForward"
import html2canvas from "html2canvas"

import { AI_AGENT_ID } from "./service/workspaceMessageService"
import { getSessionLanguage, isAIEnabled, isSecondTask, getNextSessionId } from "./service/languageConfig"
import { useTaskTimer } from "./hooks/useTaskTimer"
import { isStudentRole, showsThankYou, useStudyPhase } from "./study/useStudyPhase"
import { useAiGate } from "./study/useAiGate"
import { saveRrwebEvents } from "../../service/rrwebService"
import { fetchTransitionNotice } from "../../service/experimentService"

import {
  PRE_TASK_QUESTIONS,
  PRE_QUIZ_QUESTIONS,
  MID_SESSION_QUESTIONS,
  POST_TASK_QUESTIONS,
  getPreTaskQuestions,
  getPostTaskQuestions
} from "./constants/surveyQuestions"

import { usePanelResize } from "./hooks/usePanelResize"
import { formatTestsForRender } from "./utils/TestCases/testDataFormatter"
import { providedCodeOf } from "../session/testResults"

import { SESSION_EVENTS } from './constants/telemetryEvents'
import { PREVIEW_EVENTS } from './constants/telemetryEvents'
import { getGuiSessionConfig } from './config/guiSessionConfig'
import { testsEnabled } from '../session/sessionSettings'
import { workspaceShowsAiChat, workspaceShowsEndSurvey } from '../session/studentView/studentSurface'
import ReviewWindow from '../student-components/ReviewWindow'
import { SelectedGroupProvider } from '../../context/SelectedGroupContext'

import './css/workspace.scss'

const CLASS_DEPLOYMENT_SESSIONS = [654, 661, 643, 662, 663, 664, 665, 666, 669, 674, 675, 676, 677, 678]
const USER_STUDY_SESSIONS = [655, 658, 659, 660]
const CHI_STUDY_SESSIONS = [663, 664, 665, 666]
// Single-task CS 3114 deployments: 669 Radix, 674 hashing, 675 DFS,
// 676 general trees, 677 between(), 678 isValidBST.  Same wrapper for all.
const D2_SESSIONS = [669, 674, 675, 676, 677, 678]
const TASK_DURATION_SECONDS = 600
const D2_TASK_DURATION_SECONDS = 1800

export default function Workspace() {
  const { session } = useContext(SessionContext)
  const { currentUser } = useContext(AuthContext)
  const { codeRef, setCode } = useContext(CodeContext)
  const { runTests, isRunning, terminalOutput, testResults, testOutcomes } = useContext(TerminalContext)
  const { messages, sendMessage, isAiProcessing } = useContext(ChatContext)
  const { logTelemetryEvent, getAllEvents, setStudyDone } = useContext(TelemetryContext)
  const { hasAnswered, hasConsented, isProcessing, handleConsent } = useContext(ConsentContext)
  const { hasCompleted: hasCompletedDemographics, markCompleted } = useContext(DemographicSurveyContext)
  const { isCompleted: isSurveyCompleted, submit: submitSurvey, loading: surveyLoading } = useContext(SurveyContext)
  const { questions, isQuizSession, preCompleted, postCompleted, loading: quizLoading, submitPreQuiz, submitPostQuiz } = useContext(QuizContext)

  const guiConfig = getGuiSessionConfig(session)
  const isYCHFall2026 = Boolean(guiConfig)
  // JS Bin-style layout (see guiSessionConfig.js): no Task Description/Test Cases panel and
  // no Run Tests/Terminal strip - just the code editors and the Output/AI Assistant panel,
  // matching JS Bin's own general-purpose-playground layout rather than a graded exercise's.
  const isColumnsLayout = guiConfig?.layout === 'columns'
  // Initial/Final Comparison layout (see guiSessionConfig.js): same "no Task
  // Description/Test Cases, no Terminal" treatment as columns layout - nothing is graded
  // here - plus a pair of fixed Initial/Target reference renders above the (HTML-only)
  // editor, built in the middle-panel block below.
  const isCompareLayout = guiConfig?.layout === 'compare'
  // Initial/Final Comparison — CSS variant (see guiSessionConfig.js): same "no Task
  // Description/Test Cases, no Terminal" treatment, but a single live student view next
  // to a static Target IMAGE (not a second live render) - see the middle-panel block
  // below - and no separate editor+preview row, since that live view IS the editor's
  // preview (the professor's "only one initial view or student view this time" ask).
  const isCompareImageLayout = guiConfig?.layout === 'compare-image'
  // Initial/Final Comparison — JavaScript variant (see guiSessionConfig.js): same
  // "no Task Description/Test Cases, no Terminal" treatment as the other compare layouts,
  // plus a pair of fixed Initial/Target reference renders (both live/interactive, unlike
  // InitialFinal's html-only panes) above a THREE-file (html/css/js) editor.
  const isCompareJsLayout = guiConfig?.layout === 'compare-js'
  const hidesGradingPanels = isColumnsLayout || isCompareLayout || isCompareImageLayout || isCompareJsLayout
  const isClassDeployment = CLASS_DEPLOYMENT_SESSIONS.includes(session?.id)
  const isUserStudy = USER_STUDY_SESSIONS.includes(session?.id)
  const isCHIStudy = CHI_STUDY_SESSIONS.includes(session?.id)
  const isD2 = D2_SESSIONS.includes(session?.id)
  const language = getSessionLanguage(session?.id)
  const hasAI = isAIEnabled(session?.id)
  // hasAI is a legacy per-session CHI-study override (a couple of hardcoded session ids are
  // forced AI-off for a study condition, regardless of the instructor's toggle). chatEnabled
  // additionally requires the instructor's own "Enable AI Chat" setting - previously nothing
  // in this GUI workspace read session.enable_chat at all, so the toggle had no effect here.
  // workspaceShowsAiChat (studentSurface.js): Python-family sessions follow the instructor's
  // header switch (a plain 'Python' row has no AI chat); web and study sessions keep
  // enable_chat as before.
  const chatAllowed = hasAI && workspaceShowsAiChat(session)
  // The classroom study (study/studyConfig.js): a condition's students code until the shared
  // timer runs out or their tests all pass (about 3 s after that run), answer the concept
  // probes (ProbeModal), then see the Thank You overlay with a short survey and the answers;
  // from there they may keep working on the exercise ('extra'). studyPhase is 'none' for every
  // other session, which keeps the flow below as it was.
  const testCount = session?.test_list?.length || 0
  const passedAllTests =
    testCount > 0 && testResults?.length === testCount && testResults.every((result) => result === true)
  const {
    phase: studyPhase,
    probes,
    markProbesSaved,
    review: probeReview,
    surveyAnswered,
    finishedEarly,
    keepWorking,
    codingOver,
  } = useStudyPhase(session, currentUser, { passedAll: passedAllTests, logTelemetryEvent })
  const isStudy = studyPhase !== 'none'
  const isStudyStudent = isStudy && isStudentRole(currentUser)
  // From the probes on, the workspace underneath takes no input (inert, below): no editing,
  // no Run, no AI chat. Once they are answered there is nothing left to do in it, unless the
  // student chooses to keep working ('extra'), which opens it again.
  const workspaceLocked = studyPhase === 'probes' || studyPhase === 'done'
  // No AI chat column while the probes and the Thank You overlay are up. Only the shared timer
  // ends a condition's AI (the research lead's rule): a student who finished early and keeps
  // working ('extra') has their condition's AI until the time is up, and nobody after it.
  const studyAiOver = studyPhase === 'probes' || studyPhase === 'done' || (studyPhase === 'extra' && codingOver)
  // A delayed study condition (A5-A8) has no AI column at all until its AI time opens on the
  // server's clock - until then its page looks like the No-AI condition's - and the column
  // appears then (study/useAiGate.js, which also logs the study's AI availability events).
  // Every other session is never gated.
  const aiGate = useAiGate(session, { chatAllowed: chatAllowed && !studyAiOver, logTelemetryEvent })
  const chatEnabled = chatAllowed && !studyAiOver && !aiGate.locked
  // The study's chat messages are capped at 1000 words (ChatWindow's maxWords); every other
  // session keeps ChatWindow's own default.
  const chatMaxWords = isStudy ? 1000 : undefined
  // The Code Coach substudy's conditions (CC-O, CC-M) have a chat column of their own
  // (study/cc/CcChat.jsx: two AI roles and, in CC-M, a prediction card, all on the server's CC
  // tutor) in place of the regular chat; whether the column shows is decided as for every
  // condition, above. Once CC, always CC for this page and session, as useStudyPhase keeps its
  // study: a session update without test_code must not bring in the regular chat's tutor.
  const ccSessionRef = useRef(null)
  if (isCcStudy(session)) ccSessionRef.current = session?.id
  const isCcCondition = session?.id != null && ccSessionRef.current === session?.id
  // End Session's review survey, in the right column (studentSurface.js).
  // Students only: an instructor on Monitor's "Open Workspace" must not answer the class's
  // survey (VizmentalIDE, where it used to show, was student-only).
  const showsEndSurvey =
    !isYCHFall2026 && workspaceShowsEndSurvey(session) && (currentUser?.role === 3 || currentUser?.role === 4)
  const hasRightPanel = chatEnabled || isYCHFall2026 || showsEndSurvey
  const isSecond = isSecondTask(session?.id)
  const nextSessionId = getNextSessionId(session?.id)

  // The study's short survey on the Thank You overlay (study/useStudySurvey.js, StudySurvey).
  const studySurvey = useStudySurvey(session?.id, currentUser?.id, { enabled: isStudyStudent })
  const studySurveyDone = surveyAnswered || studySurvey.submitted
  // The study's instructions, once per session and student, right after the consent question
  // (agreed or declined) and before the coding (study/StudyInstructions.jsx).
  const [instructionsSeenFor, setInstructionsSeenFor] = useState(null)
  const instructionsOwner = `${session?.id}:${currentUser?.id}`
  const instructionsAhead =
    isStudyStudent &&
    (studyPhase === 'waiting' || studyPhase === 'coding') &&
    instructionsSeenFor !== instructionsOwner &&
    !hasSeenInstructions(session?.id, currentUser?.id)
  const showsStudyInstructions = instructionsAhead && hasAnswered === true
  // The instructions' note for a student whose AI access changed in this exercise (the research
  // lead, 2026-11-04; the server decides who), asked for as soon as the instructions are ahead -
  // while the consent form is up - so it is usually here when they show. They never wait for it:
  // a late answer adds the note to them, and a failed one leaves it out.
  const [noticeReply, setNoticeReply] = useState(null)
  useEffect(() => {
    if (!instructionsAhead) return undefined
    let cancelled = false
    fetchTransitionNotice(session?.id).then((notice) => {
      if (!cancelled) setNoticeReply({ owner: instructionsOwner, notice })
    })
    return () => {
      cancelled = true
    }
  }, [instructionsAhead, session?.id, instructionsOwner])
  const transitionNotice = noticeReply?.owner === instructionsOwner ? noticeReply.notice : null

  // Code the exercise gives the student (E3's Person class), or null: shown under the task.
  const providedCode = providedCodeOf(session?.test_code)

  const testsToRender = guiConfig?.tests
    ? guiConfig.tests.map((testName, index) => ({
        testName,
        result: testResults?.[index] ?? null,
      }))
    : formatTestsForRender(session, testResults || [], testOutcomes)

  const containerRef = useRef(null)
  const layout = usePanelResize(containerRef)
  // A delayed condition's chat column arrives mid-exercise, possibly after the student widened
  // the task panel into its space: make room so the editor keeps a usable width.
  const { fitRightPanel } = layout
  const chatShownRef = useRef(chatEnabled)
  useEffect(() => {
    if (chatEnabled && !chatShownRef.current && aiGate.unlockAtMs !== null) fitRightPanel()
    chatShownRef.current = chatEnabled
  }, [chatEnabled, aiGate.unlockAtMs, fitRightPanel])
  const livePreviewRef = useRef(null)
  // InitialFinalCSS's IDE/"Your View" split row - passed to usePanelResize's
  // startDraggingIdeSplit at drag-start since that's the row the drag percentage is
  // computed against, not the whole workspace container.
  const ideSplitRowRef = useRef(null)
  // InitialFinalJS's Target/"Your View" split column - same reasoning, vertical instead
  // of horizontal (see usePanelResize.js's startDraggingJsViewSplit).
  const jsViewSplitColRef = useRef(null)

  const [showReplay, setShowReplay] = useState(false)
  const [showPostQuiz, setShowPostQuiz] = useState(false)
  const [showPostTaskSurvey, setShowPostTaskSurvey] = useState(false)
  const [showPrizeWheel, setShowPrizeWheel] = useState(false)
  const [taskCompleted, setTaskCompleted] = useState(false)
  const [instructionsReady, setInstructionsReady] = useState(false)
  const [webDevCode, setWebDevCode] = useState("")
  const [previewRenderNumber, setPreviewRenderNumber] = useState(0)
  const [rightPanelTab, setRightPanelTab] = useState('preview')
  // InitialFinalJS only - lets a student collapse the task text to bring Target/Your View
  // higher up the column without scrolling, per the instructor's request. Starts expanded
  // (matches the layout already in use); purely a display toggle, not logged to telemetry -
  // it changes how much is visible above the fold, not what the student sees or does.
  const [taskCollapsed, setTaskCollapsed] = useState(false)

  // If the instructor turns chat off mid-session (session.enable_chat flips live via
  // SessionContext's polling/websocket sync) while a student is on the AI tab, fall back to
  // Live Preview rather than leaving them stranded on a tab that no longer has a button.
  useEffect(() => {
    if (!chatEnabled && rightPanelTab === 'ai') {
      setRightPanelTab('preview')
    }
  }, [chatEnabled, rightPanelTab])

  // Mirrors TelemetryContext's recording gate: a session that has never opted into the
  // instructor-controlled timer (duration_minutes null) keeps recording for the whole visit,
  // same as before. A session that HAS opted in only records between Start and Stop/End, per
  // "recording is only done for the duration of instructor starts the session to when the
  // instructor ends the session" - there's no student-visible "session" window to bound
  // recording to otherwise.
  const isRecordingWindowOpen = session?.duration_minutes == null || session?.status === 'active'

  // rrweb screen recording, gated on consent (hasConsented: the student agreed; declining
  // also closes the modal, but that is hasAnswered) - same pattern as VizmentalIDE.jsx (the
  // classic Python/Vizmental workspace). GUI (HTML/CSS/JS/Mixed) sessions never had this
  // wired up at all, so the "Session Replay" tab in Student Activity always showed "No
  // valid session recording found" for every GUI session, regardless of anything else being
  // logged correctly - there was simply nothing to play back. Note this only records this
  // document's DOM; LivePreview renders student HTML/CSS/JS inside a sandboxed iframe, which
  // rrweb does not capture by default, so a GUI replay's preview pane will show blank even
  // once recording exists - a separate, larger fix if that pane needs to appear in playback.
  // isRecordingWindowOpen flipping stops recording (effect cleanup) when the instructor
  // pauses/ends a timed session, and starts a fresh recording segment (a new full snapshot)
  // if they resume - rrweb-player handles multiple sequential segments in one event stream.
  // Never for the classroom study's conditions: the replay is not study data, and it is the
  // heaviest stream a page sends.
  useEffect(() => {
    if (isStudy || !session?.id || !currentUser?.id || hasConsented !== true || !isRecordingWindowOpen) return

    const eventsBuffer = []

    const stopRecording = record({
      emit(event) {
        eventsBuffer.push(event)
      }
    })

    const flushInterval = setInterval(() => {
      if (eventsBuffer.length > 0) {
        const batch = eventsBuffer.splice(0, eventsBuffer.length)
        saveRrwebEvents(session.id, currentUser.id, batch)
          .catch(err => console.error('Failed to save rrweb events:', err))
      }
    }, 10000)

    return () => {
      stopRecording()
      clearInterval(flushInterval)
      if (eventsBuffer.length > 0) {
        saveRrwebEvents(session.id, currentUser.id, eventsBuffer)
          .catch(err => console.error('Failed to save final rrweb events:', err))
      }
    }
  }, [isStudy, session?.id, currentUser?.id, hasConsented, isRecordingWindowOpen])

  // inert takes focus out of the workspace in current browsers; blurring as well covers the
  // rest, so a key press cannot still land in the editor or the chat box.
  useEffect(() => {
    const focused = document.activeElement
    if (workspaceLocked && focused && containerRef.current?.contains(focused)) focused.blur()
  }, [workspaceLocked])

  // A study condition's page is finished once 'done': its telemetry heartbeat pauses.
  useEffect(() => {
    if (isStudy) setStudyDone?.(studyPhase === 'done')
  }, [isStudy, studyPhase, setStudyDone])

  // Sync initial code for web dev preview once loaded from context
  useEffect(() => {
    if (isYCHFall2026 && guiConfig?.previewMode !== 'manual' && codeRef.current) {
      setWebDevCode(codeRef.current)
    }
  }, [isYCHFall2026, guiConfig?.previewMode, codeRef])

  // Parse code for preview if YCH web dev session
  let parsedCode = { html: "", css: "", js: "" }
  try {
    if (isYCHFall2026 && webDevCode) {
      parsedCode = JSON.parse(webDevCode)
    }
  } catch (e) {
    parsedCode = { html: webDevCode || "", css: "", js: "" }
  }

  // Compare layout's fixed "Initial" reference render always shows the starter code as
  // authored (guiConfig.starterCode), not the student's current in-progress edits (that's
  // parsedCode/webDevCode above, shown in the right panel's Live Preview instead).
  let starterParsedForCompare = { html: "", css: "", js: "" }
  try {
    if ((isCompareLayout || isCompareJsLayout) && guiConfig?.starterCode) {
      starterParsedForCompare = JSON.parse(guiConfig.starterCode)
    }
  } catch (e) {
    // Malformed starter code - reference panes just render blank.
  }

  const handleCodeChange = (newCode) => {
    setCode(newCode)
    if (isYCHFall2026 && guiConfig?.previewMode !== 'manual') {
      setWebDevCode(newCode)
    }
  }

  const getCodeVersion = (code) => {
    let hash = 2166136261
    for (let i = 0; i < code.length; i += 1) {
      hash ^= code.charCodeAt(i)
      hash = Math.imul(hash, 16777619)
    }
    return `fnv1a-${(hash >>> 0).toString(16)}`
  }

  const renderPreview = () => {
    const code = codeRef.current || guiConfig?.starterCode || ''
    const nextRenderNumber = previewRenderNumber + 1

    logTelemetryEvent(PREVIEW_EVENTS.RENDER_REQUESTED, {
      render_number: nextRenderNumber,
      code_version: getCodeVersion(code),
      enabled_files: guiConfig?.enabledFiles,
      timestamp: Date.now(),
    })

    setPreviewRenderNumber(nextRenderNumber)
    setWebDevCode(code)
  }

  // Whole-workspace screenshot for InitialFinal/InitialFinalCSS's manual chat attachment
  // (re-enabled with a region-selection step - see ChatWindow.jsx's ScreenshotRegionSelector
  // and the right-panel comment below for why this was previously off). html2canvas rasterizes
  // the outer page (panels, dividers, editor chrome) but - like most DOM-to-canvas approaches -
  // renders the live-preview iframe's own content as blank, since it's a separate document.
  // The iframe's own dependency-free requestSnapshot() (see LivePreview.jsx) is composited on
  // top afterward at the iframe's actual on-screen position so the result shows both the
  // workspace layout AND the student's real rendered output. Returns a data URL, or null if
  // the workspace container isn't mounted or capture fails outright - the caller (ChatWindow)
  // surfaces that as "try again" rather than silently attaching nothing.
  const requestWorkspaceRegionCapture = async () => {
    const container = containerRef.current
    if (!container) return null

    let baseCanvas
    try {
      baseCanvas = await html2canvas(container, { backgroundColor: '#ffffff', logging: false, scale: 1 })
    } catch (e) {
      return null
    }

    const [snapshot, iframeRect] = await Promise.all([
      livePreviewRef.current?.requestSnapshot(),
      Promise.resolve(livePreviewRef.current?.getBoundingRect?.()),
    ])

    if (snapshot?.screenshot && iframeRect) {
      const containerRect = container.getBoundingClientRect()
      const frameImg = new Image()
      const frameLoaded = await new Promise((resolve) => {
        frameImg.onload = () => resolve(true)
        frameImg.onerror = () => resolve(false)
        frameImg.src = snapshot.screenshot
      })
      if (frameLoaded) {
        try {
          const ctx = baseCanvas.getContext('2d')
          ctx.drawImage(
            frameImg,
            Math.round(iframeRect.left - containerRect.left),
            Math.round(iframeRect.top - containerRect.top),
            Math.round(iframeRect.width),
            Math.round(iframeRect.height)
          )
        } catch (e) {
          // Best-effort compositing - fall back to the outer-layout-only capture below.
        }
      }
    }

    try {
      return baseCanvas.toDataURL('image/jpeg', 0.8)
    } catch (e) {
      return null
    }
  }

  const sessionClosed = session?.status === 'closed'

  const usedAIInPrevious = new URLSearchParams(window.location.search).get('usedAI') === 'true'

  // Task timer.
  // June/D1 (CHI_STUDY_SESSIONS): 10 min, starts after pre-quiz + instructions.
  // Single-task deployments (669, 674-678): 30 min, starts after the pre-task
  // survey. The timer is the guaranteed exit path — without it, a student who
  // never passes all tests would never reach the post-survey or done screen.
  const taskDuration = isD2 ? D2_TASK_DURATION_SECONDS : TASK_DURATION_SECONDS
  const timerActive =
    (isCHIStudy && !taskCompleted && (isSecond || (isSurveyCompleted('pre_quiz') && instructionsReady))) ||
    (isD2 && !taskCompleted && isSurveyCompleted('pre_task'))
  const { isExpired, stopTimer } = useTaskTimer(
    taskDuration,
    timerActive,
    session?.id
  )

  // Timer expired — trigger survey flow
  useEffect(() => {
    if (!isExpired || taskCompleted) return

    logTelemetryEvent(SESSION_EVENTS.TASK_TIMEOUT, {
      code: codeRef.current,
      test_results: testResults,
      timestamp: Date.now()
    })

    setTaskCompleted(true)
    setShowPostTaskSurvey(true)
    // Only when the time runs out; the rest is read as it is then (the older studies' flow).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isExpired])

  // All tests pass — stop timer and trigger survey flow (D1 + single-task)
  useEffect(() => {
    if (testResults.length === 0 || !(isCHIStudy || isD2) || taskCompleted) return

    const allPassed = testResults.every(result => result === true)
    if (allPassed) {
      stopTimer()
      setTaskCompleted(true)

      logTelemetryEvent(SESSION_EVENTS.TASK_COMPLETED, {
        code: codeRef.current,
        timestamp: Date.now()
      })

      const timer = setTimeout(() => {
        setShowPostTaskSurvey(true)
      }, 3000)
      return () => clearTimeout(timer)
    }
    // Only on a run's new results; the rest is read as it is then (the older studies' flow).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [testResults])

  // Show replay modal when all tests pass (user study only)
  useEffect(() => {
    if (testResults.length === 0) return

    const allPassed = testResults.every(result => result === true)
    if (allPassed && isUserStudy) {
      const timer = setTimeout(() => {
        if (isQuizSession && !postCompleted) {
          setShowPostQuiz(true)
        } else {
          setShowReplay(true)
        }
      }, 3000)

      return () => clearTimeout(timer)
    }
    // Only on a run's new results; the rest is read as it is then (the older studies' flow).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [testResults])

  return (
    <div className="ws-container">
      {/* 1. Consent Modal (skip for second task) */}
      {!isSecond && (
        <ConsentModal
          isOpen={hasAnswered === false}
          onConsent={handleConsent}
          isProcessing={isProcessing}
          visualTraceEnabled={!!guiConfig?.visualTraceEnabled}
          manualCaptureEnabled={isYCHFall2026}
          aiVisualContextEnabled={!!guiConfig?.aiVisualContextEnabled}
        />
      )}

      {/* 1b. The study's instructions, right after the consent question (study conditions,
          students only, once per session and student) */}
      {showsStudyInstructions && (
        <StudyInstructions
          durationMinutes={session?.duration_minutes}
          hasProbes={getStudyConfig(session)?.probeSet !== null}
          notice={transitionNotice}
          onStart={() => {
            markInstructionsSeen(session?.id, currentUser?.id)
            setInstructionsSeenFor(instructionsOwner)
          }}
        />
      )}

      {/* 2. Demographic Survey (skip for second task) */}
      {!isSecond && hasAnswered && !hasCompletedDemographics && isClassDeployment && currentUser?.role !== 1 && (
        <DemographicSurvey onComplete={markCompleted} />
      )}

      {/* 3. Pre-Task Survey (D1 + single-task; skip for second task).
          Single-task deployments get topic-parameterised perception items —
          these must render BEFORE the task description is interacted with; as a
          blocking modal it covers the workspace until submitted. */}
      {!isSecond && hasAnswered && hasCompletedDemographics !== false && (isCHIStudy || isD2) && !surveyLoading && !isSurveyCompleted('pre_task') && (
        <SurveyModal
          title="Pre-Task Survey"
          subtitle="Please answer before beginning the task."
          questions={isD2 ? getPreTaskQuestions(session?.id) : PRE_TASK_QUESTIONS}
          onSubmit={(responses) => submitSurvey('pre_task', responses)}
        />
      )}

      {/* 4. Pre-Quiz (D1 only — single-task deployments have no concept quiz by design) */}
      {!isSecond && hasAnswered && isCHIStudy && !surveyLoading && isSurveyCompleted('pre_task') && !isSurveyCompleted('pre_quiz') && (
        <SurveyModal
          title="Quick Knowledge Check"
          subtitle="No worries if you're unsure — just give your best guess."
          questions={PRE_QUIZ_QUESTIONS}
          onSubmit={(responses) => submitSurvey('pre_quiz', responses)}
        />
      )}

      {/* 5. Instructions (D1 only) */}
      {!isSecond && isCHIStudy && isSurveyCompleted('pre_quiz') && !instructionsReady && (
        <InstructionsModal onReady={() => setInstructionsReady(true)} />
      )}

      {/* Pre-Quiz (existing user study flow) */}
      {hasAnswered && isQuizSession && preCompleted === false && !quizLoading && (
        <QuizModal
          open={true}
          questions={questions}
          quizType="pre"
          onSubmit={submitPreQuiz}
        />
      )}

      {/* inert as an empty string: React 18 drops a boolean on an attribute it does not know. */}
      <div className="ws-content" ref={containerRef} inert={workspaceLocked || showsStudyInstructions ? '' : undefined}>

        {/* LEFT PANEL */}
        {!hidesGradingPanels && (
          <>
            <div className="ws-panel-left" style={{ width: `${layout.leftWidth}px` }}>
              <div className="ws-panel-content">
                <TaskDescription task={session.task || guiConfig?.assignment} />
                {/* The exercise's provided code (E3's Person), read-only: every run loads it. */}
                {providedCode && <ProvidedCode code={providedCode.code} />}
                {/* Hidden when the instructor turned test cases off (enable_tests). */}
                {testsEnabled(session) && (
                  <>
                    <div className="ws-section-divider"></div>
                    <TestCases tests={testsToRender} />
                  </>
                )}
              </div>
            </div>

            {/* Left Resizer */}
            <div className="ws-resizer ws-horizontal" onMouseDown={layout.startDraggingLeft} />
          </>
        )}

        {/* LEFT PANEL - InitialFinalJS only: the CodePen-style "Your Code" editor, all
            three files stacked and always visible (see CodeEditor.jsx's `stacked` prop),
            reusing the same left-panel slot/resizer the graded types use above for their
            Task/Tests panel - this layout just puts different content in it. */}
        {isCompareJsLayout && (
          <>
            <div className="ws-panel-left" style={{ width: `${layout.leftWidth}px`, display: 'flex', flexDirection: 'column' }}>
              <CodeEditor
                initialCode={codeRef.current}
                onCodeChange={handleCodeChange}
                onTelemetry={logTelemetryEvent}
                language={language}
                isWebDev={isYCHFall2026}
                enabledFiles={guiConfig?.enabledFiles}
                starterCode={guiConfig?.starterCode}
                layout="columns"
                stacked
                paneTitle="Your Code"
              />
            </div>
            <div className="ws-resizer ws-horizontal" onMouseDown={layout.startDraggingLeft} />
          </>
        )}

        {/* MIDDLE PANEL - with the terminal, the editor ends where the terminal starts (as the
            Presenter's does), so the end of a long program can be scrolled into view: it ran on
            under the terminal, out of reach in a short window (E2 survey). */}
        <div className="ws-middle-panel" style={hidesGradingPanels ? undefined : { paddingBottom: `${layout.terminalHeight}px` }}>
          {isCompareLayout && (
            <>
              {/* Full task instructions, shown above the Initial/Target renders per the
                  instructor's request - hidesGradingPanels below hides the usual
                  left-sidebar Task Description panel for this layout, so without this the
                  assignment text (guiConfig.assignment / session.task) was never actually
                  shown to students. */}
              <div style={{ padding: '12px 16px 0' }}>
                <TaskDescription task={session.task || guiConfig?.assignment} />
              </div>
              <div style={{ display: 'flex', alignItems: 'stretch', gap: '1px', backgroundColor: '#e5e5e5', borderTop: '1px solid #e5e5e5', borderBottom: '1px solid #e5e5e5', height: '38%', minHeight: '160px' }}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  {/* Fixed reference render: the starter HTML/CSS as they stand today. Not the
                      student's own editable preview (that's the Live Preview next to the
                      editor below, fed from codesRef) - this pane never changes as the
                      student types. showRefresh hidden: the reload button reloads a fixed,
                      unchanging render, so it has no effect here and only confused the
                      professor when reviewing the layout. */}
                  <LivePreview
                    title="Initial"
                    badge={null}
                    showRefresh={false}
                    html={starterParsedForCompare.html}
                    css={starterParsedForCompare.css}
                    js=""
                  />
                </div>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: '32px', flexShrink: 0, backgroundColor: '#ffffff' }}>
                  <ArrowForward style={{ color: '#9ca3af', fontSize: '1.5rem' }} />
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  {/* Target render: same shared CSS, different HTML (see guiSessionConfig.js -
                      finalHtml deliberately carries no css of its own). Never sent to the AI
                      assistant - see systemPrompt.js/promptContextBuilder.js. */}
                  <LivePreview
                    title="Target"
                    badge={null}
                    showRefresh={false}
                    html={guiConfig?.finalHtml || ''}
                    css={starterParsedForCompare.css}
                    js=""
                  />
                </div>
              </div>
            </>
          )}
          {isCompareJsLayout && (
            /* Task / Target view / Your view, stacked vertically in this one column - no
               separate frozen "Initial" pane (dropped per the CodePen-style redesign; the
               starter behavior is still visible by reading the starter JS in the editor to
               the left). Both preview panes use chromeStyle for the browser-chrome look. */
            <div style={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0, overflowY: 'auto' }}>
              <div style={{ borderBottom: '1px solid #e5e5e5', flexShrink: 0 }}>
                {/* Collapsing this hides the task text entirely, which is what actually
                    shifts Target up in the column - the toggle itself stays put as a
                    one-line bar so it's still there to bring the task back. */}
                <div style={{ display: 'flex', justifyContent: 'flex-end', padding: '6px 16px 0' }}>
                  <button
                    onClick={() => setTaskCollapsed((collapsed) => !collapsed)}
                    style={{
                      background: 'none', border: 'none', cursor: 'pointer', padding: '2px 4px',
                      fontSize: '0.75rem', fontWeight: 600, color: '#2563eb',
                    }}
                  >
                    {taskCollapsed ? 'Show task ▾' : 'Hide task ▴'}
                  </button>
                </div>
                {!taskCollapsed && (
                  <div style={{ padding: '0 16px 12px' }}>
                    <TaskDescription task={session.task || guiConfig?.assignment} />
                  </div>
                )}
              </div>
              {/* Target/"Your View" split - draggable up/down via usePanelResize's
                  jsViewSplitPercent, the vertical counterpart to InitialFinalCSS's
                  horizontal ideSplit. ref captured here (not the whole workspace container)
                  since the drag percentage is computed against this column's own height.
                  flex: '1 0 480px' (not flex:1/minHeight:0) so a long task description can't
                  squeeze Target/Your View below a usable height - once the column's natural
                  content (task + this 480px floor) exceeds the available area, the parent's
                  overflowY:auto scrolls instead of clipping, which is what actually needed
                  fixing here (previously ws-content's overflow:hidden just clipped anything
                  past the visible area with no way to reach it). */}
              <div ref={jsViewSplitColRef} style={{ display: 'flex', flexDirection: 'column', flex: '1 0 480px' }}>
                <div style={{ height: `${layout.jsViewSplitPercent}%`, minHeight: 0, borderBottom: '1px solid #e5e5e5' }}>
                  {/* Target: same starter html/css, but the real target JS - live and
                      interactive, so students can click through the intended accordion
                      behavior themselves. Never sent to the AI assistant (see
                      systemPrompt.js/promptContextBuilder.js) - but, unlike the HTML/CSS
                      Target panes, this one IS technically inspectable via DevTools by a
                      student who goes looking, since demonstrating live behavior requires a
                      real running script. A deliberate, documented tradeoff (see the
                      InitialFinalJS comment in guiSessionConfig.js), not an oversight. */}
                  <LivePreview
                    title="Target"
                    badge="Interactive"
                    showRefresh={false}
                    chromeStyle
                    html={starterParsedForCompare.html}
                    css={starterParsedForCompare.css}
                    js={guiConfig?.finalJs || ''}
                    disableInteraction={!!layout.activeResizer}
                  />
                </div>
                <div
                  onMouseDown={(e) => layout.startDraggingJsViewSplit(e, jsViewSplitColRef.current)}
                  title="Drag to resize"
                  style={{
                    height: '6px', flexShrink: 0, cursor: 'row-resize',
                    backgroundColor: layout.activeResizer === 'jsViewSplit' ? '#2563eb' : '#e5e5e5',
                  }}
                />
                <div style={{ height: `${100 - layout.jsViewSplitPercent}%`, minHeight: 0 }}>
                  {/* Your view: the student's own live, editable preview - same component/
                      props every other type's own preview uses (Render button, refresh,
                      telemetry), just with the browser-chrome header styling. */}
                  <LivePreview
                    ref={livePreviewRef}
                    title="Your View"
                    badge="Interactive"
                    chromeStyle
                    html={parsedCode.html}
                    css={parsedCode.css}
                    js={parsedCode.js}
                    onRender={guiConfig?.previewMode === 'manual' ? renderPreview : undefined}
                    onTelemetry={logTelemetryEvent}
                    renderLabel={guiConfig?.previewLabel}
                    hasRendered={previewRenderNumber > 0}
                    disableInteraction={!!layout.activeResizer}
                    visualTraceEnabled={!!guiConfig?.visualTraceEnabled}
                    renderMeta={{
                      render_number: previewRenderNumber,
                      code_version: getCodeVersion(webDevCode || ''),
                    }}
                  />
                </div>
              </div>
            </div>
          )}
          {isCompareImageLayout && (
            /* Non-resizable reference row: Task Description beside Target, both fixed. Target
               used to sit next to "Your View" below instead, but that meant it was eating
               roughly half of the one row students actually drag - moving it up here frees
               the entire IDE/"Your View" row for the resize split, which is what actually
               needs the width. Target itself was never meant to resize alongside "Your
               View" either way (it's a flat reference image, not a second live render) - see
               the comment on the Target pane below for why. */
            <div style={{ display: 'flex', alignItems: 'stretch', gap: '1px', backgroundColor: '#e5e5e5', borderBottom: '1px solid #e5e5e5', height: '38%', minHeight: '200px' }}>
              <div style={{ flex: 1, minWidth: 0, padding: '12px 16px', overflow: 'auto', backgroundColor: '#ffffff' }}>
                <TaskDescription task={session.task || guiConfig?.assignment} />
              </div>
              <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column' }}>
                {/* Target: a plain <img> (or <video>/animated <img> for a GIF), not a
                    LivePreview - a live-rendered target here would let a student open
                    DevTools and read the exact CSS values needed (the whole "answer" for
                    this exercise), unlike InitialFinal's Target where the answer was just a
                    class/id name. See guiSessionConfig.js's InitialFinalCSS comment. Never
                    sent to the AI assistant either - see getInitialFinalCSSSystemPrompt in
                    systemPrompt.js. It also can't itself be resized to "match" Your View as
                    students drag - the intended way an instructor gives students more than
                    one reference width is by composing the image itself as a montage (or
                    uploading a GIF of the layout resizing) rather than a single still shot -
                    see the Target Image guidance in GuiExerciseEditor.jsx. */}
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 16px', borderBottom: '1px solid #e5e5e5', backgroundColor: '#ffffff' }}>
                  <span style={{ fontFamily: "'Open Sans', sans-serif", fontWeight: 600, fontSize: '0.9rem', color: '#171717' }}>Target</span>
                </div>
                <div style={{ flex: 1, overflow: 'auto', display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: '#f9fafb' }}>
                  {guiConfig?.finalImage ? (
                    <img
                      src={guiConfig.finalImage}
                      alt="Target layout the student is trying to reach"
                      style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }}
                    />
                  ) : (
                    <div style={{ color: '#9ca3af', fontSize: '0.85rem', padding: '16px', textAlign: 'center' }}>
                      No target image set. Ask your instructor to add one in the exercise editor.
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}
          {isCompareLayout ? (
            /* Professor's feedback: keep the Live Preview next to the editor (not tucked in
               the right panel, which is now chat-only) so students can see their own render
               update right beside the code producing it. */
            <div style={{ display: 'flex', flex: 1, minHeight: 0 }}>
              <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column' }}>
                <CodeEditor
                  initialCode={codeRef.current}
                  onCodeChange={handleCodeChange}
                  onTelemetry={logTelemetryEvent}
                  language={language}
                  isWebDev={isYCHFall2026}
                  enabledFiles={guiConfig?.enabledFiles}
                  starterCode={guiConfig?.starterCode}
                  layout={guiConfig?.layout}
                  hideDisabledTabs={isCompareLayout}
                  paneTitle="Your Code"
                />
              </div>
              <div style={{ flex: 1, minWidth: 0, borderLeft: '1px solid #e5e5e5' }}>
                <LivePreview
                  ref={livePreviewRef}
                  html={parsedCode.html}
                  css={parsedCode.css}
                  js={parsedCode.js}
                  onRender={guiConfig?.previewMode === 'manual' ? renderPreview : undefined}
                  onTelemetry={logTelemetryEvent}
                  renderLabel={guiConfig?.previewLabel}
                  hasRendered={previewRenderNumber > 0}
                  disableInteraction={!!layout.activeResizer}
                  visualTraceEnabled={!!guiConfig?.visualTraceEnabled}
                  mobilePreviewEnabled={!!guiConfig?.mobilePreviewEnabled}
                  renderMeta={{
                    render_number: previewRenderNumber,
                    code_version: getCodeVersion(webDevCode || ''),
                  }}
                />
              </div>
            </div>
          ) : isCompareImageLayout ? (
            /* IDE beside the student's own live view, with a real draggable divider between
               them (usePanelResize's ideSplit - see Workspace's containerEl argument below)
               instead of a resize handle inside the preview itself. Dragging this steals
               width from the editor and gives it to "Your View", so the achievable range is
               close to the full row width, not capped at half of it the way it was when
               Target sat here instead (see the comment on the row above). */
            <div ref={ideSplitRowRef} style={{ display: 'flex', flex: 1, minHeight: 0 }}>
              <div style={{ width: `${layout.ideSplitPercent}%`, minWidth: 0, display: 'flex', flexDirection: 'column' }}>
                <CodeEditor
                  initialCode={codeRef.current}
                  onCodeChange={handleCodeChange}
                  onTelemetry={logTelemetryEvent}
                  language={language}
                  isWebDev={isYCHFall2026}
                  enabledFiles={guiConfig?.enabledFiles}
                  starterCode={guiConfig?.starterCode}
                  layout={guiConfig?.layout}
                  hideDisabledTabs
                  extraReadOnlyFiles={['html']}
                  paneTitle="Your CSS"
                />
              </div>
              <div
                onMouseDown={(e) => layout.startDraggingIdeSplit(e, ideSplitRowRef.current)}
                title="Drag to resize"
                style={{
                  width: '6px', flexShrink: 0, cursor: 'col-resize',
                  backgroundColor: layout.activeResizer === 'ideSplit' ? '#2563eb' : '#e5e5e5',
                }}
              />
              <div style={{ width: `${100 - layout.ideSplitPercent}%`, minWidth: 0, display: 'flex', flexDirection: 'column' }}>
                <LivePreview
                  ref={livePreviewRef}
                  title="Your View"
                  badge="Drag divider to resize"
                  showRefresh={false}
                  html={parsedCode.html}
                  css={parsedCode.css}
                  js={parsedCode.js}
                  onRender={guiConfig?.previewMode === 'manual' ? renderPreview : undefined}
                  onTelemetry={logTelemetryEvent}
                  renderLabel={guiConfig?.previewLabel}
                  hasRendered={previewRenderNumber > 0}
                  disableInteraction={!!layout.activeResizer}
                  visualTraceEnabled={!!guiConfig?.visualTraceEnabled}
                  renderMeta={{
                    render_number: previewRenderNumber,
                    code_version: getCodeVersion(webDevCode || ''),
                  }}
                />
              </div>
            </div>
          ) : isCompareJsLayout ? null : (
            // isCompareJsLayout renders nothing here - its editor now lives in the LEFT
            // PANEL above (see the isCompareJsLayout block near the top of ws-content) and
            // its Task/Target/Your-view stack is the isCompareJsLayout block right above
            // this ternary. Nothing belongs in this slot for that layout.
            <CodeEditor
              initialCode={codeRef.current}
              onCodeChange={handleCodeChange}
              onTelemetry={logTelemetryEvent}
              language={language}
              isWebDev={isYCHFall2026}
              enabledFiles={guiConfig?.enabledFiles}
              starterCode={guiConfig?.starterCode}
              layout={guiConfig?.layout}
              hideDisabledTabs={isCompareLayout}
            />
          )}
          {!hidesGradingPanels && (
            <>
              <Terminal
                height={layout.terminalHeight}
                onRun={() => runTests(codeRef.current)}
                isRunning={isRunning}
                output={terminalOutput}
                hasTests={isYCHFall2026 || testsEnabled(session)}
              />

              <div
                className="ws-resizer ws-vertical"
                style={{ position: 'absolute', bottom: layout.terminalHeight, left: 0, right: 0, zIndex: 30 }}
                onMouseDown={layout.startDraggingTerminal}
              />
            </>
          )}
        </div>

        {/* Right Resizer */}
        {hasRightPanel && <div className="ws-resizer ws-horizontal" onMouseDown={layout.startDraggingRight} />}

        {/* RIGHT PANEL - stays mounted for any GUI (YCH) session even with chat off, since it
            also hosts LivePreview; for non-GUI sessions the panel exists only for chat. */}
        {(chatEnabled || isYCHFall2026) && (isCompareLayout || isCompareImageLayout || isCompareJsLayout) && (
          <div className="ws-panel-right" style={{ width: `${layout.rightWidth}px`, display: 'flex', flexDirection: 'column' }}>
            {/* Professor's feedback on this session type: the Live Preview now sits next to
                the editor (middle panel above) instead of here, so the entire right panel
                is just the AI Assistant. Manual screenshot attachment is re-enabled here
                (onRequestRegionCapture, below) with a region-selection step in ChatWindow -
                the student picks what to keep before it's sent, and can crop out the Target
                pane entirely if a plain attach would otherwise show the visual answer
                directly. Sending remains impossible without also typing something (see
                ChatWindow's Send-disabled-on-empty-text logic, unchanged) and the system
                prompt (see getInitialFinalCSSSystemPrompt/getInitialFinalSystemPrompt) is
                told not to diagnose an attached image on the AI's own when the student's own
                text doesn't actually describe anything specific. Record/burst stay off here -
                a multi-frame capture of the whole workspace, composited per frame, is a lot
                of extra machinery for a feature that's about describing a single moment, not
                a change over time the way the plain-render burst capture is for other types. */}
            {chatEnabled && (
              <div style={{ flex: 1, minHeight: 0 }}>
                <ChatWindow
                  messages={messages || []}
                  onSendMessage={sendMessage}
                  currentUserName={currentUser?.first_name || "Student"}
                  currentUserId={currentUser?.id}
                  aiId={AI_AGENT_ID}
                  isAiProcessing={isAiProcessing}
                  onRequestRegionCapture={requestWorkspaceRegionCapture}
                  maxWords={chatMaxWords}
                  markdownReplies={isStudy}
                  notice={<OffloadingStrip isAiProcessing={isAiProcessing} onAsk={(text, options) => sendMessage(text, undefined, options)} />}
                />
              </div>
            )}
          </div>
        )}

        {hasRightPanel && !isCompareLayout && !isCompareImageLayout && !isCompareJsLayout && (
          <div className="ws-panel-right" style={{ width: `${layout.rightWidth}px`, display: 'flex', flexDirection: 'column' }}>
            {/* Toggle header if it's a web dev session with chat enabled - just Live Preview,
                no tab bar needed, when there's nothing to toggle to. */}
            {isYCHFall2026 && chatEnabled && (
              <div style={{
                display: 'flex',
                background: '#fafafa',
                borderBottom: '1px solid #e5e5e5',
                padding: '8px 16px',
                justifyContent: 'space-between',
                alignItems: 'center'
              }}>
                <span style={{ fontSize: '0.85rem', fontWeight: 600, color: '#737373', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                  Workspace View
                </span>
                <div style={{
                  display: 'flex',
                  backgroundColor: '#f3f4f6',
                  borderRadius: '6px',
                  padding: '2px',
                  border: '1px solid #e5e5e5'
                }}>
                  <button
                    onClick={() => setRightPanelTab('preview')}
                    style={{
                      padding: '4px 12px',
                      fontSize: '0.75rem',
                      fontWeight: 600,
                      border: 'none',
                      borderRadius: '4px',
                      backgroundColor: rightPanelTab === 'preview' ? '#ffffff' : 'transparent',
                      color: rightPanelTab === 'preview' ? '#171717' : '#737373',
                      boxShadow: rightPanelTab === 'preview' ? '0 1px 3px rgba(0,0,0,0.1)' : 'none',
                      cursor: 'pointer',
                      transition: 'all 0.15s ease'
                    }}
                  >
                    {guiConfig?.previewTabLabel || 'Live Preview'}
                  </button>
                  <button
                    onClick={() => setRightPanelTab('ai')}
                    style={{
                      padding: '4px 12px',
                      fontSize: '0.75rem',
                      fontWeight: 600,
                      border: 'none',
                      borderRadius: '4px',
                      backgroundColor: rightPanelTab === 'ai' ? '#ffffff' : 'transparent',
                      color: rightPanelTab === 'ai' ? '#171717' : '#737373',
                      boxShadow: rightPanelTab === 'ai' ? '0 1px 3px rgba(0,0,0,0.1)' : 'none',
                      cursor: 'pointer',
                      transition: 'all 0.15s ease'
                    }}
                  >
                    AI Assistant
                  </button>
                </div>
              </div>
            )}

            {/* Both stay mounted for GUI sessions (toggled via display, not unmounted) so the
                preview iframe survives switching to the AI Assistant tab - the chat's
                screenshot/record buttons need a live iframe to capture from even while
                LivePreview itself isn't the visible tab. Only mounted at all when the
                instructor's "Enable AI Chat" toggle is on. */}
            {chatEnabled && (
              <div style={{ display: (!isYCHFall2026 || rightPanelTab === 'ai') ? 'contents' : 'none' }}>
                {isCcCondition ? (
                  <CcChat
                    sessionId={session?.id}
                    currentUserName={currentUser?.first_name || "Student"}
                    maxWords={chatMaxWords}
                  />
                ) : (
                  <ChatWindow
                    messages={messages || []}
                    onSendMessage={sendMessage}
                    currentUserName={currentUser?.first_name || "Student"}
                    currentUserId={currentUser?.id}
                    aiId={AI_AGENT_ID}
                    isAiProcessing={isAiProcessing}
                    onRequestScreenshot={isYCHFall2026 ? () => livePreviewRef.current?.requestSnapshot() : undefined}
                    onRequestBurst={isYCHFall2026 ? (durationMs, intervalMs, onFrame) => livePreviewRef.current?.requestBurst(durationMs, intervalMs, onFrame) : undefined}
                    maxWords={chatMaxWords}
                    markdownReplies={isStudy}
                    notice={<OffloadingStrip isAiProcessing={isAiProcessing} onAsk={(text, options) => sendMessage(text, undefined, options)} />}
                  />
                )}
              </div>
            )}
            {showsEndSurvey && (
              // ReviewWindow reads the selected group, a /session-only context; it is
              // provided here just for the survey.
              <div style={{ flex: 1, minHeight: 0, overflow: 'auto' }}>
                <SelectedGroupProvider>
                  <ReviewWindow />
                </SelectedGroupProvider>
              </div>
            )}
            {isYCHFall2026 && (
              <div style={{ display: rightPanelTab === 'preview' ? 'contents' : 'none' }}>
                <LivePreview
                  ref={livePreviewRef}
                  html={parsedCode.html}
                  css={parsedCode.css}
                  js={parsedCode.js}
                  onRender={guiConfig?.previewMode === 'manual' ? renderPreview : undefined}
                  onTelemetry={logTelemetryEvent}
                  renderLabel={guiConfig?.previewLabel}
                  hasRendered={previewRenderNumber > 0}
                  disableInteraction={!!layout.activeResizer}
                  visualTraceEnabled={!!guiConfig?.visualTraceEnabled}
                  mobilePreviewEnabled={!!guiConfig?.mobilePreviewEnabled}
                  renderMeta={{
                    render_number: previewRenderNumber,
                    code_version: getCodeVersion(webDevCode || ''),
                  }}
                />
              </div>
            )}
          </div>
        )}
      </div>

      {/* Post-Task Survey (D1: mid-session/post depending on arm;
          single-task: topic-parameterised post items) */}
      {showPostTaskSurvey && (isCHIStudy || isD2) && (
        <SurveyModal
          title={isD2 ? "Post-Task Survey" : (hasAI ? "Mid-Session Survey" : "Post-Task Survey")}
          subtitle={isD2
            ? "Great job! Please answer a few final questions, then spin the prize wheel!"
            : (hasAI
              ? "Great work! The next task will be similar but without AI assistance. Before moving on, please answer a few questions."
              : "Great job! Please answer a few final questions, then spin the prize wheel!")}
          questions={isD2
            ? getPostTaskQuestions(session?.id)
            : (hasAI
              ? (messages.length > 1
                ? MID_SESSION_QUESTIONS
                : MID_SESSION_QUESTIONS.filter(q => q.key === 'understanding'))
              : POST_TASK_QUESTIONS.filter(q => !q.aiOnly || usedAIInPrevious))}
          onSubmit={async (responses) => {
            await submitSurvey(isD2 ? 'post_task' : (hasAI ? 'mid_session' : 'post_task'), responses)
            setShowPostTaskSurvey(false)

            if (!isD2 && nextSessionId) {
              const usedAI = messages.length > 1
              localStorage.removeItem(`timer_start_${nextSessionId}`)
              localStorage.removeItem(`timer_done_${nextSessionId}`)
              window.location.href = `/workspace/${nextSessionId}?usedAI=${usedAI}`
            } else {
              setShowPrizeWheel(true)
            }
          }}
        />
      )}

      {/* Prize Wheel (after post-task survey) */}
      <PrizeWheelModal
        isOpen={showPrizeWheel}
        onClose={() => setShowPrizeWheel(false)}
      />

      {/* Post-Quiz (existing user study flow) */}
      {showPostQuiz && isQuizSession && !postCompleted && (
        <QuizModal
          open={true}
          questions={questions}
          quizType="post"
          onSubmit={async (responses) => {
            await submitPostQuiz(responses)
            setShowPostQuiz(false)
            setShowReplay(true)
          }}
        />
      )}

      {/* Concept probes (study conditions, once the coding time is up) */}
      {studyPhase === 'probes' && (
        <ProbeModal
          sessionId={session?.id}
          userId={currentUser?.id}
          probes={probes}
          onSaved={markProbesSaved}
          passedAll={finishedEarly}
        />
      )}

      {/* Thank You (non-CHI class deployments). A study condition shows it once the server has
          its probe answers, whatever the status, and never before: its session closes when
          the instructor's page ends the time, which can be a moment before the coding time
          is up here, and on a pause (showsThankYou). A study condition's student then has the
          short survey (StudySurvey), the probes' answers with their explanations once the server
          sends them (ProbeReview), and, once the survey is in, "Keep working on the exercise"
          ('extra': the workspace again, with the condition's AI until the shared time is up). */}
      {showsThankYou({ phase: studyPhase, sessionClosed, isCHIStudy }) && (
        <div className="survey-overlay">
          <div className={`survey-modal survey-thankyou${probeReview || isStudyStudent ? ' probe-review-modal' : ''}`}>
            <div className="survey-check">✓</div>
            <h2>Thank You!</h2>
            <p>Your responses have been recorded.</p>
            {studyPhase === 'done' && isStudyStudent && (
              <StudySurvey answered={studySurveyDone} refused={studySurvey.refused} onSubmit={studySurvey.submit} />
            )}
            {studyPhase === 'done' && probeReview && <ProbeReview items={probes.items} review={probeReview} />}
            {studyPhase === 'done' && isStudyStudent && studySurveyDone && (
              <div className="study-continue">
                <button type="button" className="survey-submit" onClick={keepWorking}>
                  Keep working on the exercise
                </button>
              </div>
            )}
            <p className="survey-subtext">You may now close this window.</p>
          </div>
        </div>
      )}

      {showReplay && isUserStudy && (
        <ReplayModal
          onClose={() => setShowReplay(false)}
          events={getAllEvents()}
          task={session?.task}
          testCases={formatTestsForRender(session, [])}
          starterCode={''}
          sessionId={session?.id}
          studentUserId={currentUser?.id}
          labelerName={`${currentUser?.first_name} ${currentUser?.last_name}` || currentUser?.email}
          studentName={currentUser?.first_name || 'Student'}
        />
      )}
    </div>
  )
}
